import React, { useMemo } from 'react';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { RTUData, FirewallRuleEntry } from '../../types/RtuData';
import { StaticCompositeTable } from './StaticCompositeTable';
import { RtuStaticModuleCardTitle } from './RtuStaticModuleCardTitle';

interface FirewallRow {
  port: string;
  status: string;
}

/**
 * Resolves firewall rule rows from the available snapshot shape.
 *
 * Priority order:
 * 1. Structured `firewall.rules` map (typed, emitted by backend mapper)
 * 2. Parsed `firewall.rulesJson` string (legacy stringified JSON format)
 */
function deriveRows(data: RTUData | null | undefined): {
  rows: FirewallRow[];
  configDisplay: string;
} {
  const snapshot = data?.rtuStaticData?.firewall;
  const configDisplay = snapshot?.config ?? '—';

  if (snapshot?.rules && Object.keys(snapshot.rules).length > 0) {
    const rows = Object.values(snapshot.rules).map((entry: FirewallRuleEntry): FirewallRow => ({
      port:   entry.portNo ?? '—',
      status: entry.status ?? '—',
    }));
    return { rows, configDisplay };
  }

  const raw = snapshot?.rulesJson;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') {
    return { rows: [], configDisplay };
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed) && typeof parsed === 'object' && parsed !== null) {
      const rows: FirewallRow[] = [];
      let parsedConfig = configDisplay;

      for (const [key, value] of Object.entries(parsed)) {
        const lk = key.toLowerCase();
        if (lk === 'config') { parsedConfig = String(value); continue; }
        if (lk === 'total')  { continue; }

        if (typeof value === 'object' && value !== null) {
          const entry = value as Record<string, unknown>;
          rows.push({
            port:   String(entry.Port_no ?? entry.port_no ?? '—'),
            status: String(entry.Status  ?? entry.status  ?? '—'),
          });
        }
      }

      return { rows, configDisplay: parsedConfig };
    }

    const arr: FirewallRow[] = (Array.isArray(parsed) ? parsed : [parsed]).map((rule: unknown) => {
      if (typeof rule !== 'object' || rule === null) {
        return { port: String(rule), status: '—' };
      }
      const r = rule as Record<string, unknown>;
      return {
        port:   String(r.Port_no ?? r.port_no ?? '—'),
        status: String(r.Status  ?? r.status  ?? '—'),
      };
    });

    return { rows: arr, configDisplay };

  } catch {
    return { rows: [], configDisplay };
  }
}

interface Props {
  data: RTUData | null | undefined;
}

/**
 * RtuFirewallCard
 *
 * Displays firewall rules from RTU static data as a Port / Status table.
 *
 * Used in legacy-v2 static-data section and the enhanced connectivity tab.
 */
export const RtuFirewallCard = ({ data }: Props) => {
  const { t } = useTranslation();

  const { rows, configDisplay } = useMemo(() => deriveRows(data), [data]);

  return (
    <Card
      title={
        <RtuStaticModuleCardTitle
          title={t('rtuDetail.staticData.firewall.title', { defaultValue: 'Firewall' })}
          status={configDisplay}
        />
      }
      className="compact-card flat-card rtu-static-module-card"
    >
      <StaticCompositeTable
        rows={rows}
        columns={[
          { field: 'port',   header: t('rtuDetail.staticData.firewall.colPort',   { defaultValue: 'Port'   }), width: '6rem' },
          {
            field: 'status',
            header: t('rtuDetail.staticData.firewall.colStatus', { defaultValue: 'Status' }),
            statusTone: true,
            statusIconTooltips: {
              positive: 'rtuDetail.staticData.firewall.statusIcon.positive',
              negative: 'rtuDetail.staticData.firewall.statusIcon.negative',
            },
          },
        ]}
        emptyMessage={t('table.noRowsFound', { defaultValue: 'No firewall rules found.' })}
      />
    </Card>
  );
};

export default RtuFirewallCard;
